export default {
  name: "PageControl",
  props: {
    title: {
      type: String,
      default: "Report PDF",
      required: false,
    },
    elements_query: {
      type: String,
      default: ".sheet",
      required: false,
    },
    id: {
      type: String,
      default: () =>
        "pdf-control-" + Math.random().toString(36).substring(2, 11),
      required: false,
    },
  },
  data() {
    return {
      selected_quality: "medium",
      modal_instance: null,
      modal_id: "",
    };
  },
  mounted() {
    // Set unique modal ID based on component instance ID
    this.modal_id = `pdf-quality-modal-${this.id}`;
  },
  methods: {
    show_quality_modal() {
      if (!this.modal_instance) {
        // Initialize the modal if it hasn't been initialized yet
        const modal_element = document.getElementById(this.modal_id);
        if (modal_element) {
          this.modal_instance = $(modal_element).modal();
        }
      }

      if (this.modal_instance) {
        this.modal_instance.modal("show");
      }
    },

    async download_pdf() {
      // Hide the modal if it's open
      if (this.modal_instance) {
        this.modal_instance.modal("hide");
        this.modal_instance = null; // Clear the instance
      }

      const elements = document.querySelectorAll(this.elements_query);
      const total_pages = elements.length;

      // Check if document is too large
      if (total_pages > 100) {
        const result = await Swal.fire({
          title: "Large Document Warning",
          text: `This document has ${total_pages} pages. Generation may take several minutes. Continue?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Continue",
          cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;
      }

      let cancelled = false;

      Swal.fire({
        title: "Creating PDF...",
        html: `
          <div>Processing page 0 of ${total_pages}</div>
          <div style="margin-top: 10px;">
            <progress id="pdf-progress" value="0" max="${total_pages}" style="width: 100%;"></progress>
          </div>
        `,
        allowOutsideClick: false,
        showCancelButton: total_pages > 50,
        cancelButtonText: "Cancel",
        showConfirmButton: false,
        preConfirm: () => {
          cancelled = true;
          return false;
        },
      });

      try {
        await this.ensure_libraries_loaded();

        const { jsPDF } = window.jspdf;
        const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });

        let first_page = true;

        for (let i = 0; i < elements.length && !cancelled; i++) {
          const element = elements[i];
          const current_page = i + 1;

          // Set scale based on selected quality
          let scale = 1.5; // Default medium quality
          if (this.selected_quality === "low") {
            scale = 1.1;
          } else if (this.selected_quality === "high") {
            scale = 2.0;
          }

          // Store original images before html2canvas processes them
          const original_images = [];
          const images_in_element = element.querySelectorAll('img');
          images_in_element.forEach((img, idx) => {
            if (img.src && img.src.startsWith('data:image')) {
              const rect = img.getBoundingClientRect();
              const element_rect = element.getBoundingClientRect();
              original_images.push({
                src: img.src,
                x: rect.left - element_rect.left,
                y: rect.top - element_rect.top,
                width: rect.width,
                height: rect.height,
                id: 'original-img-' + idx
              });
              img.setAttribute('data-original-image-id', 'original-img-' + idx);
            }
          });

          const canvas = await window.html2canvas(element, {
            scale: scale,
            logging: false,
            backgroundColor: "#ffffff",
            allowTaint: true,
            useCORS: true,
            imageTimeout: 0,
          });

          // Update progress
          const progress_element = document.getElementById("pdf-progress");
          if (progress_element) {
            progress_element.value = current_page;
          }

          Swal.update({
            html: `
              <div>Processing page ${current_page} of ${total_pages}</div>
              <div style="margin-top: 10px;">
                <progress id="pdf-progress" value="${current_page}" max="${total_pages}" style="width: 100%;"></progress>
              </div>
            `,
          });

          // Convert canvas to image
          const img_data = canvas.toDataURL("image/png", 1.0);
          const img_props = doc.getImageProperties(img_data);
          const pdf_width = doc.internal.pageSize.getWidth();
          const pdf_height = (img_props.height * pdf_width) / img_props.width;

          if (!first_page) doc.addPage();

          doc.setFillColor(255, 255, 255);
          doc.rect(0, 0, pdf_width, pdf_height, "F");
          
          // Add the background canvas
          doc.addImage(
            img_data,
            "PNG",
            0,
            0,
            pdf_width,
            pdf_height,
            undefined,
            "FAST"
          );

          // Now overlay the original high-quality images on top
          for (const orig_img of original_images) {
            try {
              // Calculate scaled position and size
              const scaled_x = (orig_img.x * scale * pdf_width) / (canvas.width);
              const scaled_y = (orig_img.y * scale * pdf_height) / (canvas.height);
              const scaled_width = (orig_img.width * scale * pdf_width) / (canvas.width);
              const scaled_height = (orig_img.height * scale * pdf_height) / (canvas.height);

              // Add original high-quality image directly to PDF
              doc.addImage(
                orig_img.src,
                scaled_x,
                scaled_y,
                scaled_width,
                scaled_height,
                undefined,
                "NONE"
              );
            } catch (e) {
              console.warn('Could not add original image:', e);
            }
          }

          first_page = false;

          // Force garbage collection every 10 pages
          if (current_page % 10 === 0) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            if (window.gc) window.gc();
          }
        }

        if (!cancelled) {
          Swal.update({
            title: "Saving PDF...",
            html: "Finalizing document...",
            showCancelButton: false,
          });

          doc.save(this.title + ".pdf");
          Swal.fire({
            icon: "success",
            title: "PDF Generated Successfully",
            text: `Your ${total_pages}-page PDF has been downloaded in ${this.selected_quality} quality.`,
            timer: 3000,
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "PDF Generation Failed",
          text: error.message || "An error occurred while generating the PDF.",
        });
      } finally {
        if (!cancelled) {
          Swal.close();
        }
      }
    },
    async ensure_libraries_loaded() {
      // Define load_script first so we can use it
      const load_script = (src) =>
        new Promise((resolve, reject) => {
          if (document.querySelector(`script[src="${src}"]`)) {
            // Already loaded
            const check_loaded = () => {
              if (
                (src.includes("jspdf") && window.jspdf) ||
                (src.includes("html2canvas") && window.html2canvas)
              ) {
                resolve();
              } else {
                setTimeout(check_loaded, 50);
              }
            };
            check_loaded();
            return;
          }
          const s = document.createElement("script");
          s.src = src;
          s.async = false;
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      await load_script(
        "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"
      );
      await load_script(
        "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"
      );

      // Also ensure Bootstrap is loaded for the modal if needed
      if (typeof bootstrap === "undefined") {
        await load_script(
          "https://cdn.jsdelivr.net/npm/bootstrap@5.2.3/dist/js/bootstrap.bundle.min.js"
        );
      }
    },
  },
};
